/**
 * CABCostPanel — Displays cost breakdown for CAB generation.
 *
 * Shows total cost, per-phase breakdown, and token usage.
 * Collapsible display in the action bar area.
 */

import React, { useState } from 'react';
import { Text, Icon } from '@ui-library';
import { IconName } from '@ui-library/Icon/Icon.type';
import { formatCost } from '../../utils/formatCost';
import { showTokens } from '../../utils/usageConfig';
import type { CABCostInfo } from '../../types/cab';

interface CABCostPanelProps {
  cost: CABCostInfo;
  visible: boolean;
  totalDurationSeconds?: number | null;
}

/** Format token count. */
function formatTokens(count: number): string {
  if (count >= 1_000_000) return `${(count / 1_000_000).toFixed(1)}M`;
  if (count >= 1_000) return `${(count / 1_000).toFixed(1)}K`;
  return `${count}`;
}

/** Format a duration in seconds to a human-readable string (matches Build Mode). */
function formatDuration(seconds: number): string {
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const mins = Math.floor(seconds / 60);
  const remainingSeconds = Math.round(seconds % 60);
  return `${mins}m ${remainingSeconds}s`;
}

/** Phase & section display names. */
const PHASE_NAMES: Record<string, string> = {
  file_processing: 'Reading Files',
  code_analysis: 'Analyzing Code',
  skeleton_map: 'Discovering Sections',
  skeleton_reduce: 'Organizing Sections',
  skeleton_assemble: 'Finalizing Document Outline',
  vector_indexing: 'Indexing Knowledge Base',
  section_generation: 'Generating Sections',
  // Individual sections
  projectIdentity: 'Project Identity',
  roles: 'User Roles',
  dataModel: 'Data Model',
  screens: 'Screens & UI',
  workflows: 'Workflows',
  reporting: 'Reports',
  integrations: 'Integrations',
  rules: 'Business Rules',
  userStories: 'User Stories',
  suggestedQuestions: 'Suggested Questions',
};

export const CABCostPanel: React.FC<CABCostPanelProps> = ({ cost, visible, totalDurationSeconds }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!visible || cost.total_usd === 0) return null;

  return (
    <div className="border-t border-border-secondary">
      {/* Collapsed summary */}
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="
          w-full px-bg-v2-xl py-bg-v2-sm
          flex items-center justify-between
          text-text-tertiary hover:text-text-secondary
          transition-colors
        "
      >
        <div className="flex items-center gap-bg-v2-sm">
          <Icon name={IconName.Dollar} size="xs" inheritColor />
          <Text variant="text-xs" weight="medium" className="text-text-tertiary">
            Generation Cost: {formatCost(cost.total_usd)}
            {totalDurationSeconds != null && totalDurationSeconds > 0 && (
              <span className="text-text-quaternary">
                {' '}·{' '}{formatDuration(totalDurationSeconds)}
              </span>
            )}
          </Text>
        </div>
        <Icon
          name={IconName.ChevronDown}
          size="xs"
          className={`transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
          inheritColor
        />
      </button>

      {/* Expanded breakdown */}
      {isExpanded && (
        <div className="px-bg-v2-xl pb-bg-v2-md">
          {/* Per-phase costs */}
          <div className="mb-bg-v2-sm">
            {Object.entries(cost.breakdown || {}).map(([phase, data]) => {
              if (!data || typeof data !== 'object') return null;
              const phaseCost = (data as any).cost_usd || 0;
              const requests = (data as any).requests || (data as any).calls || 0;
              if (phaseCost === 0) return null;

              return (
                <div key={phase} className="flex justify-between items-center py-bg-v2-xxs">
                  <Text variant="text-xs" className="text-text-tertiary">
                    {PHASE_NAMES[phase] || phase}
                    <span className="text-text-quaternary ml-bg-v2-xs">
                      ({requests} {requests === 1 ? 'Request' : 'Requests'})
                    </span>
                  </Text>
                  <Text variant="text-xs" weight="medium" className="text-text-secondary">
                    {formatCost(phaseCost)}
                  </Text>
                </div>
              );
            })}
          </div>

          {/* Token usage with costs */}
          <div className={showTokens ? "border-t border-border-secondary pt-bg-v2-sm" : ""}>
            {showTokens && (
              <>
                <div className="flex justify-between items-center py-bg-v2-xxs">
                  <Text variant="text-xs" className="text-text-tertiary">
                    Input Tokens
                    <span className="text-text-quaternary ml-bg-v2-xs">
                      ({formatTokens(cost.token_usage?.total_input_tokens || 0)})
                    </span>
                  </Text>
                  <Text variant="text-xs" weight="medium" className="text-text-secondary">
                    {formatCost(cost.token_usage?.input_tokens_cost || 0)}
                  </Text>
                </div>
                <div className="flex justify-between items-center py-bg-v2-xxs">
                  <Text variant="text-xs" className="text-text-tertiary">
                    Output Tokens
                    <span className="text-text-quaternary ml-bg-v2-xs">
                      ({formatTokens(cost.token_usage?.total_output_tokens || 0)})
                    </span>
                  </Text>
                  <Text variant="text-xs" weight="medium" className="text-text-secondary">
                    {formatCost(cost.token_usage?.output_tokens_cost || 0)}
                  </Text>
                </div>
                {(cost.token_usage?.total_cache_write_tokens || 0) > 0 && (
                  <div className="flex justify-between items-center py-bg-v2-xxs">
                    <Text variant="text-xs" className="text-text-tertiary">
                      Cache Creation
                      <span className="text-text-quaternary ml-bg-v2-xs">
                        ({formatTokens(cost.token_usage.total_cache_write_tokens)})
                      </span>
                    </Text>
                    <Text variant="text-xs" weight="medium" className="text-text-secondary">
                      {formatCost(cost.token_usage?.cache_write_tokens_cost || 0)}
                    </Text>
                  </div>
                )}
                {(cost.token_usage?.total_cache_read_tokens || 0) > 0 && (
                  <div className="flex justify-between items-center py-bg-v2-xxs">
                    <Text variant="text-xs" className="text-text-tertiary">
                      Cache Read
                      <span className="text-text-quaternary ml-bg-v2-xs">
                        ({formatTokens(cost.token_usage.total_cache_read_tokens)})
                      </span>
                    </Text>
                    <Text variant="text-xs" weight="medium" className="text-text-secondary">
                      {formatCost(cost.token_usage?.cache_read_tokens_cost || 0)}
                    </Text>
                  </div>
                )}
              </>
            )}

            {/* Total cost & duration */}
            <div className="flex justify-between items-center pt-bg-v2-xs mt-bg-v2-xs border-t border-border-secondary">
              <Text variant="text-xs" weight="semibold" className="text-text-secondary">
                Total Cost:
              </Text>
              <Text variant="text-xs" weight="semibold" className="text-text-secondary">
                {formatCost(cost.total_usd)}
              </Text>
            </div>
            {totalDurationSeconds != null && totalDurationSeconds > 0 && (
              <div className="flex justify-between items-center pt-bg-v2-xxs">
                <Text variant="text-xs" weight="semibold" className="text-text-secondary">
                  Total Time:
                </Text>
                <Text variant="text-xs" weight="semibold" className="text-text-secondary">
                  {formatDuration(totalDurationSeconds)}
                </Text>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
